import React from 'react';
import { Cell } from 'react-table';
import cx from 'classnames';

import { IconMusicFile, Tooltip } from '@trqk/components';

import { musicFileIconWidth, rowHeight } from '../../constants';

interface WorkNameCellProps {
    cell: Cell<TrqkGapChannelAudio, GapWorkNameCellValue>;
    column: StatementWorkColumn;
    /**
     * When this is false, indentation is used to align the cell vertically with other instances of
     * the same component that have `showIcon === true`.
     */
    showIcon?: boolean;
}

const labelClasses =
    'respond-to-mouse leading-tight clamp-1-line break-words px-1 text-xs';

/**
 * Displays identifying details about a row in the ConfirmWorks table. If the `detectionArtist` and
 * `detectionTitle` are available, they are each shown on a separate line. If not, and
 * `audioFilename` is available, it is shown instead.
 * @param props - {@link WorkNameCellProps}
 * @returns JSX
 */
export function WorkNameCell({
    cell,
    column,
    showIcon = true,
}: WorkNameCellProps): JSX.Element {
    const width = (column?.width as number) ?? 100;
    const indentWidth = showIcon ? 0 : musicFileIconWidth;
    const finalWidth = width - musicFileIconWidth - indentWidth;
    const audioFilename = cell?.value?.audioFilename ?? null;
    const detectionArtist = cell?.value?.detectionArtist ?? null;
    const detectionTitle = cell?.value?.detectionTitle ?? null;

    if (detectionArtist === null && detectionTitle === null && audioFilename) {
        return (
            <Tooltip
                message={audioFilename}
                position="bottom"
                minWidth={finalWidth}
                maxWidth={finalWidth}
                zIndex={50}
            >
                <div
                    className="flex items-center"
                    style={{
                        height: rowHeight,
                        marginLeft: `${indentWidth}px`,
                    }}
                >
                    {showIcon && (
                        <IconMusicFile
                            fillColor="secondary-500"
                            width={musicFileIconWidth}
                        />
                    )}
                    <p
                        className="text-xs leading-tight respond-to-mouse clamp-2-lines-break-words"
                        style={{
                            width: finalWidth,
                        }}
                    >
                        {audioFilename}
                    </p>
                </div>
            </Tooltip>
        );
    }

    return (
        <Tooltip
            message={
                <div>
                    {detectionTitle && (
                        <p className="font-medium">{detectionTitle}</p>
                    )}
                    {detectionArtist && <p>{detectionArtist}</p>}
                </div>
            }
            position="bottom"
            minWidth={finalWidth}
            maxWidth={finalWidth}
            zIndex={50}
        >
            <div
                className="flex items-center"
                style={{ height: rowHeight, marginLeft: `${indentWidth}px` }}
            >
                {showIcon && (
                    <IconMusicFile
                        fillColor="secondary-500"
                        width={musicFileIconWidth}
                    />
                )}
                <div>
                    {detectionTitle && (
                        <p
                            className={cx(labelClasses, 'font-medium ')}
                            style={{
                                width: finalWidth,
                            }}
                        >
                            {detectionTitle}
                        </p>
                    )}
                    {detectionArtist && (
                        <p
                            className={cx(labelClasses)}
                            style={{
                                width: finalWidth,
                            }}
                        >
                            {detectionArtist}
                        </p>
                    )}
                </div>
            </div>
        </Tooltip>
    );
}
